<script setup>
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import Button from 'dashboard/components-next/button/Button.vue';
import {
  getLilithScenarios,
  createLilithScenario,
  updateLilithAssistant,
} from 'dashboard/api/lilithTeam';
import { createLilithFileDocument } from 'dashboard/api/lilithKnowledge';

const props = defineProps({
  assistant: { type: Object, required: true },
  canManage: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
});
const emit = defineEmits(['configuration', 'assistantUpdated']);
const { accountId } = useAccount();
const { t } = useI18n();
const scenarios = ref([]);
const ready = ref(false);
const saving = ref(false);
const error = ref('');
const notice = ref('');
const lines = value =>
  (Array.isArray(value) ? value : [])
    .map(item =>
      typeof item === 'string' ? item : item.content || item.description || ''
    )
    .filter(Boolean)
    .join('\n');
const split = value =>
  value
    .split('\n')
    .map(item => item.trim())
    .filter(Boolean);
const form = reactive({
  scenario_ids: [],
  scenarios: [],
  knowledge: '',
  guidelines: lines(props.assistant.response_guidelines),
  guardrails: lines(props.assistant.guardrails),
});
const configuration = computed(() => ({
  scenario_ids: form.scenario_ids,
  scenarios: form.scenarios.map(item => ({
    client_id: item.client_id,
    title: item.title,
    description: item.description,
    instruction: item.instruction,
  })),
  knowledge: form.knowledge,
  response_guidelines: split(form.guidelines),
  guardrails: split(form.guardrails),
}));
watch(
  configuration,
  value => {
    if (ready.value) emit('configuration', value);
  },
  { deep: true }
);
onMounted(async () => {
  try {
    const response = await getLilithScenarios(
      accountId.value,
      props.assistant.id,
      props.canManage
    );
    scenarios.value = response.data?.payload || [];
    form.scenario_ids = scenarios.value
      .filter(item => item.enabled)
      .map(item => item.id);
    ready.value = true;
    emit('configuration', configuration.value);
  } catch (e) {
    error.value = e?.response?.data?.error || t('TEAM_LILITH.PLAYGROUND.ERROR');
  }
});
const persist = async action => {
  if (!props.canManage || props.disabled || saving.value) return;
  saving.value = true;
  error.value = '';
  notice.value = '';
  try {
    await action();
    notice.value = t('TEAM_LILITH.PLAYGROUND.SAVED');
  } catch (e) {
    error.value =
      e?.response?.data?.error ||
      e?.response?.data?.detail ||
      t('TEAM_LILITH.PLAYGROUND.ERROR');
  } finally {
    saving.value = false;
  }
};
const saveControls = () =>
  persist(async () => {
    await updateLilithAssistant(accountId.value, props.assistant.id, {
      response_guidelines: split(form.guidelines),
      guardrails: split(form.guardrails),
    });
    emit('assistantUpdated');
  });
const saveKnowledge = () =>
  persist(() =>
    createLilithFileDocument(
      accountId.value,
      props.assistant.id,
      new File([form.knowledge], 'playground-reference.txt', {
        type: 'text/plain',
      }),
      'Playground reference'
    )
  );
const addScenario = () =>
  form.scenarios.push({
    client_id: crypto.randomUUID(),
    title: '',
    description: '',
    instruction: '',
    saved: false,
  });
const saveScenario = item =>
  persist(async () => {
    await createLilithScenario(accountId.value, props.assistant.id, {
      title: item.title,
      description: item.description,
      instruction: item.instruction,
      enabled: false,
    });
    item.saved = true;
  });
</script>

<template>
  <details
    class="max-h-80 shrink-0 overflow-y-auto rounded-lg border border-n-weak p-3 text-sm text-n-slate-12"
  >
    <summary>{{ $t('TEAM_LILITH.PLAYGROUND.SETUP') }}</summary>
    <p class="my-3 text-n-slate-11">
      {{ $t('TEAM_LILITH.PLAYGROUND.TEMP_NOTE') }}
    </p>
    <fieldset :disabled="disabled || saving || !ready" class="space-y-3">
      <div>
        <p>{{ $t('TEAM_LILITH.PLAYGROUND.SCENARIOS') }}</p>
        <label
          v-for="item in scenarios"
          :key="item.id"
          class="mr-4 inline-flex items-center gap-2"
        >
          <input
            v-model="form.scenario_ids"
            type="checkbox"
            :value="item.id"
          />{{ item.title }}
        </label>
      </div>
      <label
        v-for="key in ['guidelines', 'guardrails']"
        :key="key"
        class="block"
      >
        {{ $t(`TEAM_LILITH.PLAYGROUND.${key.toUpperCase()}`) }}
        <textarea
          v-model="form[key]"
          rows="3"
          maxlength="10000"
          class="mt-1 w-full rounded border border-n-weak bg-n-background p-2"
        />
      </label>
      <Button
        v-if="canManage"
        :label="$t('TEAM_LILITH.PLAYGROUND.SAVE_CONTROLS')"
        @click="saveControls"
      />
      <label class="block">
        {{ $t('TEAM_LILITH.PLAYGROUND.KNOWLEDGE') }}
        <textarea
          v-model="form.knowledge"
          rows="4"
          maxlength="10000"
          class="mt-1 w-full rounded border border-n-weak bg-n-background p-2"
        />
      </label>
      <Button
        v-if="canManage"
        :disabled="!form.knowledge.trim()"
        :label="$t('TEAM_LILITH.PLAYGROUND.SAVE_KNOWLEDGE')"
        @click="saveKnowledge"
      />
      <div
        v-for="item in form.scenarios"
        :key="item.client_id"
        class="space-y-2 rounded border border-n-weak p-3"
      >
        <label
          v-for="key in ['title', 'description', 'instruction']"
          :key="key"
          class="block"
        >
          {{
            $t(
              `TEAM_LILITH.PLAYGROUND.${{ title: 'NAME', description: 'TRIGGER', instruction: 'INSTRUCTION' }[key]}`
            )
          }}
          <textarea
            v-model="item[key]"
            :maxlength="key === 'instruction' ? 20000 : 500"
            :rows="key === 'instruction' ? 4 : 2"
            class="w-full rounded border border-n-weak bg-n-background p-2"
          />
        </label>
        <Button
          v-if="canManage"
          :disabled="
            item.saved || !item.title || !item.description || !item.instruction
          "
          :label="$t('TEAM_LILITH.PLAYGROUND.SAVE_SCENARIO')"
          @click="saveScenario(item)"
        />
        <p v-if="item.saved">
          {{ $t('TEAM_LILITH.PLAYGROUND.SCENARIO_SAVED') }}
        </p>
        <Button
          icon="i-lucide-trash"
          color="ruby"
          variant="ghost"
          :label="$t('TEAM_LILITH.SCENARIOS.DELETE')"
          @click="
            form.scenarios = form.scenarios.filter(other => other !== item)
          "
        />
      </div>
      <Button
        :disabled="form.scenarios.length >= 20"
        :label="$t('TEAM_LILITH.PLAYGROUND.ADD_SCENARIO')"
        @click="addScenario"
      />
    </fieldset>
    <p v-if="notice" class="mt-2 text-n-teal-11">{{ notice }}</p>
    <p v-if="error" class="mt-2 text-n-ruby-11">{{ error }}</p>
  </details>
</template>
